import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';

import {
    ElementQuery,
    Modal,
} from 'wanderu-components';

import { DEPART_DIRECTION } from '../../constants';
import FilterMenu from './FilterMenu/FilterMenu';
import MobileFilters from './MobileFilters/MobileFilters';
import Events from './events/MobileHeaderEvents';

import styles from './MobileHeader.scss';

const VERTICAL_TRIGGER = 56;
const SCROLL_PADDING = 10;

const transitionClasses = {
    enter: styles.enter,
    enterActive: styles.enterActive,
    enterDone: styles.enterDone,
    exit: styles.exit,
    exitActive: styles.exitActive,
    exitDone: styles.exitDone,
};

class MobileHeader extends Component {
    static propTypes = {
        activeSortType: PropTypes.string,
        children: PropTypes.node,
        departDate: PropTypes.string.isRequired,
        destinationCityName: PropTypes.string,
        direction: PropTypes.string,
        numberOfActiveFilters: PropTypes.number,
        onMapClick: PropTypes.func,
        onResetAllFilters: PropTypes.func,
        onToggleFilterMenu: PropTypes.func,
        originCityName: PropTypes.string,
        returnDate: PropTypes.string,
        searchParams: PropTypes.object,
        setSortType: PropTypes.func,
        showMap: PropTypes.bool,
        sortOptions: PropTypes.object,
        totalTrips: PropTypes.number,
        url: PropTypes.string,
    };

    static defaultProps = {
        activeSortType: '',
        destinationCityName: '',
        direction: DEPART_DIRECTION,
        numberOfActiveFilters: 0,
        onResetAllFilters: () => null,
        onToggleFilterMenu: () => null,
        originCityName: '',
        returnDate: '',
        searchParams: {},
        setSortType: () => null,
        sortOptions: {},
        totalTrips: 0,
        url: '/',
    };

    constructor(props) {
        super(props);

        this.state = {
            activeSortType: props.activeSortType,
            containerWidth: undefined,
            isFilterMenuOpen: false,
            isMediumScreen: false,
            scrollTriggered: false,
        };

        this.lastScrollTop = 0;
    }

    componentDidMount() {
        if (!this.state.isMediumScreen) {
            window.addEventListener('scroll', this.detectScroll);
        }
    }

    componentWillUnmount() {
        if (!this.state.isMediumScreen) {
            window.removeEventListener('scroll', this.detectScroll);
        }
    }

    detectScroll = () => {
        const { scrollTriggered } = this.state;
        const { scrollY } = window;

        if (scrollY <= VERTICAL_TRIGGER && scrollTriggered) {
            this.setState(() => ({ scrollTriggered: false }));
        }

        if (
            scrollY > VERTICAL_TRIGGER
            && scrollY <= this.lastScrollTop
        ) {
            this.setState(() => ({ scrollTriggered: true }));
        }

        if (scrollY > this.lastScrollTop + SCROLL_PADDING) {
            this.setState(() => ({ scrollTriggered: false }));
        }

        this.lastScrollTop = scrollY;
    };

    onToggleFilterMenu = () => {
        this.setState((oldState) => ({
            isFilterMenuOpen: !oldState.isFilterMenuOpen,
        }));

        this.props.onToggleFilterMenu();
    };

    onSelectSortType = (type) => {
        Events.sortOptionSelected({
            direction: this.props.direction,
            previousType: this.state.activeSortType,
            searchParams: this.props.searchParams,
            type,
        });

        this.setState(() => ({
            activeSortType: type,
        }), () => {
            setTimeout(() => {
                this.props.setSortType(type);

                window.scrollTo({
                    behavior: 'smooth',
                    left: 0,
                    top: 0,
                });
            }, 0);
        });
    };

    renderFilterMenu() {
        const {
            children,
            numberOfActiveFilters,
            onResetAllFilters,
            totalTrips,
        } = this.props;

        const showReset = numberOfActiveFilters > 0;

        return (
            <Modal>
                <div className={styles.filterContainer}>
                    <div className={styles.filterContainerInner}>
                        <FilterMenu
                            onCloseMenu={this.onToggleFilterMenu}
                            onReset={onResetAllFilters}
                            showReset={showReset}
                            totalTrips={totalTrips}
                        >
                            {children}
                        </FilterMenu>
                    </div>
                </div>
            </Modal>
        );
    }

    onQueryChange = (queryResults) => {
        this.setState(() => ({
            containerWidth: queryResults.width,
        }));
    };

    checkScreenSize = (queries) => {
        const {
            isMediumScreen,
        } = this.state;

        if (queries.isMediumScreen === isMediumScreen) {
            return;
        }

        this.setState(() => {
            // see SIZE_QUERY for what gets applied to state via ElementQuery
            return queries;
        });
    };

    render() {
        const {
            departDate,
            destinationCityName,
            direction,
            numberOfActiveFilters,
            onMapClick,
            originCityName,
            returnDate,
            showMap,
            sortOptions,
            url,
        } = this.props;
        const {
            activeSortType,
            containerWidth,
            isFilterMenuOpen,
            isMediumScreen,
            scrollTriggered,
        } = this.state;

        const queryElement = typeof window !== 'undefined'
            ? document.body
            : null;

        return (
            <ElementQuery
                element={queryElement}
                onChange={this.checkScreenSize}
                passAsProps={false}
            >
                {!isMediumScreen && scrollTriggered && (
                    <div className={styles.headroomPlaceholder} />
                )}
                <CSSTransition
                    classNames={transitionClasses}
                    in={!isMediumScreen && scrollTriggered}
                    timeout={{ enter: 300, exit: 0 }}
                >
                    {() => (
                        <div className={styles.container}>
                            <ElementQuery
                                onChange={this.onQueryChange}
                                passAsProps={false}
                                dimensionsOnly
                            />

                            <MobileFilters
                                activeSortType={activeSortType}
                                containerWidth={containerWidth}
                                departDate={departDate}
                                destinationCityName={destinationCityName}
                                direction={direction}
                                numberOfActiveFilters={numberOfActiveFilters}
                                onMapClick={onMapClick}
                                onSelectSortType={this.onSelectSortType}
                                onToggleFilterMenu={this.onToggleFilterMenu}
                                originCityName={originCityName}
                                returnDate={returnDate}
                                showMap={showMap}
                                sortOptions={sortOptions}
                                url={url}
                            />
                        </div>
                    )}
                </CSSTransition>

                {isFilterMenuOpen && this.renderFilterMenu()}
            </ElementQuery>
        );
    }
}

export default MobileHeader;
